import { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { obtenerJuegos, obtenerHorasReservadas, crearReserva } from "../api";
import { useUser } from "../context/AuthContext";
import ParticleBackground from "../components/ParticleBackground";
import {
  CalendarIcon,
  ClockIcon,
  UserGroupIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  GlobeAltIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";

const Checkout = () => {
  const navigate = useNavigate();
  const { user, isLoggedIn } = useUser() || {};
  const [horasOcupadas, setHorasOcupadas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { gameId } = useParams();
  const location = useLocation();
  const [juego, setJuego] = useState(null);
  // Si viene ?publicJoin=YYYY-MM-DDTHH:mm:ss.sssZ, prellenar fecha y hora
  const publicJoin = new URLSearchParams(location.search).get('publicJoin');
  let initialDate = "";
  let initialStart = "";
  if (publicJoin) {
    const dt = new Date(publicJoin);
    initialDate = dt.toISOString().slice(0, 10);
    initialStart = dt.toTimeString().slice(0, 5);
  }
  const [formData, setFormData] = useState({
    date: initialDate,
    start: initialStart,
    players: 1,
    nombre_cliente: "",
    email: "",
    telefono: "",
    publica: !publicJoin, // Si es join, no es pública
  });
  const [multiplier, setMultiplier] = useState(1);
  const [mensaje, setMensaje] = useState("");

  useEffect(() => {
    const fetchGame = async () => {
      const juegos = await obtenerJuegos();
      const selected = juegos.find((g) => g.id === parseInt(gameId));
      setJuego(selected);
    };
    fetchGame();
  }, [gameId]);

  // Prefill user data if logged in
  useEffect(() => {
    if (!isLoggedIn || !user) return;
    setFormData((prev) => ({
      ...prev,
      nombre_cliente: user.name || prev.nombre_cliente,
      email: user.email || prev.email,
      telefono: user.telefono || prev.telefono,
    }));
  }, [isLoggedIn, user]);

  useEffect(() => {
    if (!formData.date) return;
    const fetchHoras = async () => {
      const intervals = await obtenerHorasReservadas(formData.date);
      setHorasOcupadas(intervals);
    };
    fetchHoras();
  }, [formData.date]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const calcularPrecioFinal = () => {
    if (!juego) return 0;
    const jugadores = parseInt(formData.players) || 0;
    const extra = Math.max(0, jugadores - juego.capacidad_base);
    const base =
      parseFloat(juego.precio_base) +
      extra * parseFloat(juego.precio_extra_por_jugador);
    return (base * multiplier).toFixed(2);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Custom validation since form has noValidate
    const errors = [];
    const emailRegex = /.+@.+\..+/;
    if (!formData.date) errors.push("Date is required.");
    if (!formData.start) errors.push("Start time is required.");
    if (!formData.players || parseInt(formData.players) < 1) errors.push("Players must be at least 1.");
    if (!formData.nombre_cliente?.trim()) errors.push("Full Name is required.");
    if (!formData.email?.trim() || !emailRegex.test(formData.email)) errors.push("A valid email is required.");
    if (!formData.telefono?.trim()) errors.push("Phone is required.");

    if (errors.length > 0) {
      setMensaje(`❌ Please complete all required fields:\n- ${errors.join("\n- ")}`);
      setShowModal(true);
      return;
    }

    setIsLoading(true);

    let redirected = false;
    try {
      const [year, month, day] = formData.date.split("-").map(Number);
      const [hour, minute] = formData.start.split(":").map(Number);

      // Fecha y hora local directamente desde partes numéricas
      const startDt = new Date(year, month - 1, day, hour, minute, 0, 0);
      const endDt = new Date(
        startDt.getTime() + juego.duracion * multiplier * 60000
      );
      const formatCanadianDateTime = (date) => {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        const hours = date.getHours().toString().padStart(2, "0");
        const minutes = date.getMinutes().toString().padStart(2, "0");
        return `${year}-${month}-${day} ${hours}:${minutes}`;
      };

      const reservaData = {
        game_id: parseInt(gameId),
        start_time: formatCanadianDateTime(startDt),
        end_time: formatCanadianDateTime(endDt),
        players: parseInt(formData.players),
        nombre_cliente: formData.nombre_cliente,
        email: formData.email,
        telefono: formData.telefono,
        multiplier,
        es_publica: formData.publica ? 1 : 0,
      };

      console.debug("[Checkout] Submitting reservation:", reservaData);

      const res = await crearReserva(reservaData);
      const endHours = endDt.getHours().toString().padStart(2, "0");
      const endMinutes = endDt.getMinutes().toString().padStart(2, "0");
      const endStr = `${endHours}:${endMinutes}`;
      const totalNum = parseFloat(calcularPrecioFinal());
      const players = parseInt(formData.players);
      const ppp = players > 0 ? Number((totalNum / players).toFixed(2)) : totalNum;
      navigate('/thanks', {
        state: {
          game_id: parseInt(gameId),
          gameName: juego?.nombre,
          date: formData.date,
          start: formData.start,
          end: endStr,
          players,
          nombre_cliente: formData.nombre_cliente,
          email: formData.email,
          telefono: formData.telefono,
          precio_por_jugador: ppp,
          type: formData.publica ? 'public' : 'private',
          reference: res?.reference,
        }
      });
      redirected = true;
      return;
    } catch (err) {
      console.error("[Checkout] Error creating reservation:", err);
      setMensaje("❌ Error making the reservation. Please try again later.");
    } finally {
      if (!redirected) setShowModal(true);
      setIsLoading(false);
    }
  };

  if (!juego)
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center">
        <div className="text-2xl text-purple-400 font-audiowide animate-pulse">
          Loading game...
        </div>
      </div>
    );

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-gray-100 font-inter py-12 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <ParticleBackground />
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-2xl p-8 max-w-md w-full shadow-xl">
            <div className="flex flex-col items-center text-center">
              {mensaje.includes("✅") ? (
                <CheckCircleIcon className="h-16 w-16 text-green-500 mb-4" />
              ) : (
                <XCircleIcon className="h-16 w-16 text-red-500 mb-4" />
              )}
              <h3 className="text-xl font-audiowide mb-4">
                {mensaje.includes("✅")
                  ? "Reservation Successful!"
                  : "Reservation Error"}
              </h3>
              <p className="text-gray-300 mb-6">{mensaje}</p>
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-audiowide text-center mb-12 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
          Reserve: {juego.nombre}
        </h1>

        <div className="grid lg:grid-cols-2 gap-12">
          <form
            id="reservationForm"
            onSubmit={handleSubmit}
            className="space-y-8 bg-gray-800/50 rounded-2xl p-8 shadow-xl"
            noValidate
          >
            <div className="space-y-6">
              {/* Date */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Date
                </label>
                <div className="relative">
                  <CalendarIcon className="h-5 w-5 text-blue-400 absolute left-3 top-3.5" />
                  <input
                    type="date"
                    name="date"
                    required
                    onChange={handleChange}
                    className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
              {/* Start Time */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Start Time
                </label>
                <div className="relative">
                  <ClockIcon className="h-5 w-5 text-blue-400 absolute left-3 top-3.5" />
                  <select
                    name="start"
                    onChange={handleChange}
                    required
                    className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg appearance-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Select start time</option>
                    {Array.from({ length: 21 }, (_, i) => {
                      const hour = 10 + Math.floor(i / 2);
                      const minute = i % 2 === 0 ? "00" : "30";
                      const horaStr = `${hour
                        .toString()
                        .padStart(2, "0")}:${minute}`;

                      const slotStart = new Date(`1970-01-01T${horaStr}:00`);
                      const slotEnd = new Date(
                        slotStart.getTime() +
                          juego.duracion * multiplier * 60000
                      );

                      const isDisabled = horasOcupadas.some((interval) => {
                        const [start, end] = interval
                          .split("–")
                          .map((t) => new Date(`1970-01-01T${t}:00`));
                        return slotStart < end && slotEnd > start;
                      });

                      return (
                        <option
                          key={horaStr}
                          value={horaStr}
                          disabled={isDisabled}
                        >
                          {horaStr} {isDisabled && "(Unavailable)"}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              {/* Sessions Multiplier */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Sessions
                </label>
                <select
                  value={multiplier}
                  onChange={(e) => setMultiplier(+e.target.value)}
                  className="w-full py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {[1, 2, 3, 4].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>


              {/* Players */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Players
                </label>
                <div className="relative">
                  <UserGroupIcon className="h-5 w-5 text-blue-400 absolute left-3 top-3.5" />
                  <input
                    type="number"
                    name="players"
                    min={1}
                    max={juego.capacidad_maxima}
                    value={formData.players}
                    onChange={handleChange}
                    className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <p className="mt-2 text-sm text-gray-400">
                  Base: {juego.capacidad_base} | Max: {juego.capacidad_maxima}
                </p>
              </div>

              {/* Public/Private Room - Prominent Callout */}
              <div
                className={
                  `mt-2 rounded-xl p-4 border transition-colors ` +
                  (formData.publica
                    ? "border-green-500/40 bg-green-900/20"
                    : "border-purple-500/40 bg-gray-900/40")
                }
              >
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-black/30">
                    {formData.publica ? (
                      <GlobeAltIcon className="h-7 w-7 text-green-400" />
                    ) : (
                      <LockClosedIcon className="h-7 w-7 text-purple-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <label htmlFor="publica" className="text-base md:text-lg font-semibold text-gray-100">
                        Room visibility
                      </label>
                      <span
                        className={
                          `px-3 py-1 rounded-full text-xs font-semibold ` +
                          (formData.publica
                            ? "bg-green-500/20 text-green-300 border border-green-500/30"
                            : "bg-purple-500/20 text-purple-300 border border-purple-500/30")
                        }
                      >
                        {formData.publica ? "Public" : "Private"}
                      </span>
                    </div>
                    <p className="text-sm text-gray-400 mt-1">
                      {formData.publica
                        ? "Public: other players can find and join your room if there are available spots."
                        : "Private: only you and your invited group can play in this session."}
                    </p>
                    <div className="mt-3 flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="publica"
                        name="publica"
                        checked={formData.publica}
                        onChange={handleChange}
                        className="h-5 w-5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded transform scale-110"
                        disabled={!!publicJoin}
                      />
                      <label htmlFor="publica" className="text-sm md:text-base text-gray-200 select-none">
                        {formData.publica ? (
                          <>
                            Make this a <span className="font-semibold text-green-400">public room</span>
                            {" "}(others can join)
                          </>
                        ) : (
                          <>
                            Keep it a <span className="font-semibold text-purple-300">private room</span>
                            {" "}(only your group)
                          </>
                        )}
                        {publicJoin && (
                          <span className="ml-2 text-green-400 align-middle">(Joining existing public room)</span>
                        )}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Customer Info */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <UserIcon className="h-5 w-5 text-blue-400 absolute left-3 top-3.5" />
                  <input
                    type="text"
                    name="nombre_cliente"
                    required
                    value={formData.nombre_cliente}
                    onChange={handleChange}
                    className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email
                </label>
                <div className="relative">
                  <EnvelopeIcon className="h-5 w-5 text-blue-400 absolute left-3 top-3.5" />
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Phone
                </label>
                <div className="relative">
                  <PhoneIcon className="h-5 w-5 text-blue-400 absolute left-3 top-3.5" />
                  <input
                    type="tel"
                    name="telefono"
                    required
                    value={formData.telefono}
                    onChange={handleChange}
                    className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </form>

          {/* Reservation Summary */}
          <div className="bg-gray-800/50 rounded-2xl p-8 shadow-xl border-l-4 border-purple-500">
            <h2 className="text-3xl font-audiowide mb-6 bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              Reservation Summary
            </h2>

            <div className="space-y-4 text-gray-300">
              <div className="flex justify-between items-center pb-2 border-b border-gray-700">
                <span className="font-medium">Game:</span>
                <span className="text-gray-100">{juego.nombre}</span>
              </div>

              <div className="flex justify-between items-center pb-2 border-b border-gray-700">
                <span className="font-medium">Duration:</span>
                <span className="text-blue-400">
                  {juego.duracion * multiplier} minutes
                </span>
              </div>

              <div className="flex justify-between items-center pb-2 border-b border-gray-700">
                <span className="font-medium">Players:</span>
                <span className="text-purple-400">{formData.players}</span>
              </div>

              <div className="flex justify-between items-center pb-2 border-b border-gray-700">
                <span className="font-medium">Sessions:</span>
                <span className="text-green-400">{multiplier}</span>
              </div>

              <div className="flex justify-between items-center pb-2 border-b border-gray-700">
                <span className="font-medium">Visibility:</span>
                <span className={formData.publica ? "text-green-400" : "text-purple-300"}>
                  {formData.publica ? "Public" : "Private"}
                </span>
              </div>

              <div className="flex justify-between items-center pt-4">
                <span className="text-xl font-bold">Total:</span>
                <span className="text-2xl font-audiowide text-green-400">
                  ${calcularPrecioFinal()}
                </span>
              </div>
            </div>

            <div className="mt-6">
              <div className="flex items-center gap-2 mb-3">
                <InformationCircleIcon className="h-6 w-6 text-purple-400" />
                <h3 className="text-lg font-audiowide text-gray-100">
                  Game Description
                </h3>
              </div>
              <p className="text-gray-400 leading-relaxed whitespace-pre-line">
                {juego.descripcion}
              </p>
            </div>

            <div className="mt-8 p-4 bg-gray-900/50 rounded-lg">
              <p className="text-sm text-gray-400">
                <span className="block font-medium mb-1 text-purple-300">
                  Pricing Info:
                </span>
                Base price includes {juego.capacidad_base} players. Each
                additional player (up to {juego.capacidad_maxima}) costs $
                {juego.precio_extra_por_jugador}. Price multiplied by sessions.
              </p>
            </div>

            {/* Payment & Arrival Notice */}
            <div className="mt-6 p-4 bg-yellow-900/30 border border-yellow-700 rounded-lg">
              <div className="flex items-start gap-3">
                <InformationCircleIcon className="h-5 w-5 text-yellow-400 mt-0.5" />
                <p className="text-sm text-yellow-100">
                  Payment is collected at the venue on the day of your reservation. Please arrive <span className="font-semibold">10 minutes early</span> for check-in and briefing.
                </p>
              </div>
            </div>

            <div className="mt-8">
              <button
                form="reservationForm"
                type="submit"
                disabled={isLoading}
                className="w-full py-4 px-6 bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 rounded-lg font-audiowide text-lg transition-all relative disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <svg
                      className="animate-spin h-6 w-6 mr-3 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  "Confirm Reservation"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
